import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Language, Translations, translations } from '../i18n/translations';

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

/** Detect the browser / OS language; fall back to 'de' as the default. */
function detectLanguage(): Language {
  const stored = localStorage.getItem('mahmedix-lang') as Language | null;
  if (stored === 'en' || stored === 'de') return stored;

  const browserLang = navigator.language || (navigator as any).userLanguage || '';
  return browserLang.toLowerCase().startsWith('en') ? 'en' : 'de';
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(detectLanguage);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('mahmedix-lang', lang);
    document.documentElement.lang = lang;
  }, []);

  // Set the <html lang> on first render
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = translations[language];

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used inside <LanguageProvider>');
  return ctx;
}
